import { WorkplaceRequestComment } from "../types/workplace-request-comments";
import {
  CreateWorkplaceRequestParams,
  CreateWorkplaceRequestShareLinkParams,
  UpdateWorkplaceRequestParams,
  UpdateWorkplaceRequestsBatchParams,
  WorkplaceRequestApprovalResponse,
  WorkplaceRequestElement,
  WorkplaceRequestShareLink,
  WorkplaceRequestShareLinksParams,
  WorkplaceRequestShareLinksResponse,
  WorkplaceRequestStatsResponse,
  WorkplaceRequestStatus,
  WorkplaceRequestsBatchUpdateResponse,
  WorkplaceRequestsParams,
  WorkplaceRequestsResponse,
  WorkplaceRequestTicketWorkflowsResponse,
  WorkplaceRequestUploadResponse,
  WorkplaceRequestWorkflowOverrides
} from "../types/workplace-requests";
import { BaseSettingsEndpoints } from "./base";

const WORKPLACE_REQUESTS_BASE_PATH = "/v1/tickets";

export class WorkplaceRequestsClient extends BaseSettingsEndpoints {
  async getWorkplaceRequests(
    params: WorkplaceRequestsParams,
    options: { isAdmin?: boolean } = {}
  ) {
    const {
      type_id,
      tz,
      status,
      office_id,
      resource_id,
      submitter_user_id,
      owner_user_id,
      managed,
      approval_filter,
      unassigned,
      query,
      cursor,
      limit,
      start,
      end
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.set("tz", tz);

    if (type_id && type_id.length > 0) {
      type_id.forEach((id) => queryParams.append("type_id", id));
    }

    if (status && status.length > 0) {
      status.forEach((s) => queryParams.append("status", s));
    }

    if (office_id && office_id.length > 0) {
      office_id.forEach((id) => queryParams.append("office_id", id));
    }

    if (resource_id && resource_id.length > 0) {
      resource_id.forEach((id) => queryParams.append("resource_id", id));
    }

    if (submitter_user_id && submitter_user_id.length > 0) {
      submitter_user_id.forEach((id) =>
        queryParams.append("submitter_user_id", id)
      );
    }

    if (owner_user_id && owner_user_id.length > 0) {
      owner_user_id.forEach((id) => queryParams.append("owner_user_id", id));
    }

    if (managed) {
      queryParams.set("managed", "1");
    }

    if (approval_filter) {
      queryParams.set("approval_filter", approval_filter);
    }

    if (unassigned) {
      queryParams.set("unassigned", "1");
    }

    if (query) {
      queryParams.set("query", query);
    }

    if (cursor) {
      queryParams.set("cursor", cursor);
    }

    if (limit) {
      queryParams.set("limit", limit.toString());
    }

    if (start) {
      queryParams.set("start", start);
    }

    if (end) {
      queryParams.set("end", end);
    }

    const queryString = queryParams.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}${queryString ? `?${queryString}` : ""}`;

    return this.get<WorkplaceRequestsResponse>(url);
  }

  /**
   * Dashboard counts per category (pending approval, unresolved, needs triage,
   * in progress, resolved, assigned to me, my requests), each broken down by
   * type and scoped to the caller's visibility.
   */
  async getStats() {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/stats`;

    return this.get<WorkplaceRequestStatsResponse>(url);
  }

  async getWorkplaceRequestsBySchedule(scheduleId: string) {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/schedule/${scheduleId}`;
    return this.get<WorkplaceRequestElement[]>(url);
  }

  /**
   * `tz` decides the zone the returned timestamps are expressed in, the same
   * way the list endpoint's does. Without it the two disagree: a caller that
   * lists with a zone and then opens one request reverses the detail's
   * timestamps with a zone they were never in, and the age comes out shifted
   * by the offset.
   */
  async getWorkplaceRequestById(requestId: string, tz?: string) {
    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }
    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${requestId}${
      queryString ? `?${queryString}` : ""
    }`;
    return this.get<WorkplaceRequestElement>(url);
  }

  async createWorkplaceRequest(request: CreateWorkplaceRequestParams) {
    const { tz, ...body } = request;

    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }

    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}${queryString ? `?${queryString}` : ""}`;

    return this.post<
      Omit<CreateWorkplaceRequestParams, "tz">,
      WorkplaceRequestElement
    >(url, body);
  }

  async updateWorkplaceRequestStatus(
    requestId: string,
    status: WorkplaceRequestStatus,
    comment?: string,
    tz?: string
  ) {
    return this.updateWorkplaceRequest(
      requestId,
      { status, update_comment: comment },
      tz
    );
  }

  async updateWorkplaceRequestOwner(
    requestId: string,
    ownerUserId: string | null,
    tz?: string
  ) {
    return this.updateWorkplaceRequest(
      requestId,
      { owner_user_id: ownerUserId },
      tz
    );
  }

  async updateWorkplaceRequest(
    ticketId: string,
    updates: UpdateWorkplaceRequestParams,
    tz?: string
  ) {
    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }

    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}${queryString ? `?${queryString}` : ""}`;

    return this.update<UpdateWorkplaceRequestParams, WorkplaceRequestElement>(
      url,
      updates
    );
  }

  /**
   * Update several tickets in one call. Partial success: tickets that can be
   * updated are, and each failure is reported on its own entry of `results`.
   * The whole call fails (400) only on a malformed request, e.g. a duplicate or
   * invalid ticket id, or more than 100 tickets.
   */
  async updateWorkplaceRequests(
    updatesByTicketId: Record<string, UpdateWorkplaceRequestParams>,
    tz?: string
  ) {
    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }

    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}${queryString ? `?${queryString}` : ""}`;

    return this.update<
      UpdateWorkplaceRequestsBatchParams,
      WorkplaceRequestsBatchUpdateResponse
    >(url, { tickets: updatesByTicketId });
  }

  async deleteWorkplaceRequest(ticketId: string) {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}`;

    return this.delete(url);
  }

  /**
   * Approve or reject a request whose type requires approval. Returns the
   * updated request with its new `approval_status`.
   *
   * `skipWorkflowIds` names the workflows the approver chose not to run: each
   * gets a `skipped` run recorded instead of being executed. Only valid
   * alongside `approve`, and a workflow may not be both skipped and overridden.
   */
  async respondToWorkplaceRequestApproval(
    ticketId: string,
    status: WorkplaceRequestApprovalResponse,
    tz?: string,
    workflowOverrides?: WorkplaceRequestWorkflowOverrides,
    skipWorkflowIds?: string[]
  ) {
    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }

    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/approval${queryString ? `?${queryString}` : ""}`;

    return this.post<
      {
        status: WorkplaceRequestApprovalResponse;
        workflow_overrides?: WorkplaceRequestWorkflowOverrides;
        skip_workflow_ids?: string[];
      },
      WorkplaceRequestElement
    >(url, {
      status,
      ...(workflowOverrides ? { workflow_overrides: workflowOverrides } : {}),
      ...(skipWorkflowIds && skipWorkflowIds.length > 0
        ? { skip_workflow_ids: skipWorkflowIds }
        : {})
    });
  }

  /**
   * The enabled workflows the ticket's type would run on `trigger` (defaults to
   * approval), each with the values it resolved from the ticket.
   */
  async getWorkplaceRequestWorkflows(ticketId: string, trigger?: string) {
    const params = new URLSearchParams();
    if (trigger) {
      params.set("trigger", trigger);
    }
    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/workflows${queryString ? `?${queryString}` : ""}`;

    return this.get<WorkplaceRequestTicketWorkflowsResponse>(url);
  }

  /**
   * Request a signed URL to upload a file for a `file_upload` form field. Upload
   * the file to `upload_url`, then submit the returned `path` as the field value.
   */
  async generateUploadUrl(filename: string) {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/uploads`;

    return this.post<{ filename: string }, WorkplaceRequestUploadResponse>(
      url,
      {
        filename
      }
    );
  }

  async addComment(ticketId: string, comment: string, tz?: string) {
    const params = new URLSearchParams();
    if (tz) {
      params.set("tz", tz);
    }

    const queryString = params.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/comments${queryString ? `?${queryString}` : ""}`;

    return this.post<{ comment: string }, WorkplaceRequestComment>(url, {
      comment
    });
  }

  /**
   * Create a guest share link for a request. Returns the link including its
   * `token`, which guests present via the `X-Tactic-Ticket-Share-Code` header.
   * Requires the request's `capabilities.can_share`.
   */
  async createShareLink(
    ticketId: string,
    params: CreateWorkplaceRequestShareLinkParams
  ) {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/sharing`;

    return this.post<
      CreateWorkplaceRequestShareLinkParams,
      WorkplaceRequestShareLink
    >(url, params);
  }

  /** List the guest share links created for a request. */
  async getShareLinks(
    ticketId: string,
    params: WorkplaceRequestShareLinksParams = {}
  ) {
    const { cursor, limit } = params;

    const queryParams = new URLSearchParams();
    if (cursor) {
      queryParams.set("cursor", cursor);
    }
    if (limit) {
      queryParams.set("limit", limit.toString());
    }

    const queryString = queryParams.toString();
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/sharing${queryString ? `?${queryString}` : ""}`;

    return this.get<WorkplaceRequestShareLinksResponse>(url);
  }

  /** Revoke a guest share link, immediately cutting off access through it. */
  async deleteShareLink(ticketId: string, linkId: string) {
    const url = `${WORKPLACE_REQUESTS_BASE_PATH}/${ticketId}/sharing/${linkId}`;

    return this.delete(url);
  }
}
